import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import fileSize from 'filesize';
import {
  Row,
  Col,
  Button,
  Input,
  Modal,
  Upload as UploadAntd,
  message,
  List,
  Form,
  Breadcrumb,
  Skeleton,
  Tabs,
  Checkbox,
  Radio,
} from 'antd';
import immutable from 'seamless-immutable';
import { Upload, Trash, FolderPlus, Edit } from 'react-feather';
import {
  InboxOutlined,
  HomeOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons';
import FileIcon from './FileIcon';
//Utils
import { getServer } from '../../Utils/url';
import { getFileType, naturalSort } from '../../Utils/parse';
//Forms
import FolderForm from '../Form/Folder.form';
import LoadingButton from './LoadingButton';
import PreviewButton from './PreviewButton';
import ReminderForm from '../Form/Reminder.form';

//Hooks
import { useAuth } from '../../Hooks/Auth.hook';
import { useFetchUserFilesPath } from '../../Hooks/Files.hook';
import {
  createFolder,
  deleteFile,
  deleteFolder,
  updateFile,
} from '../../Services/API';

const { Dragger } = UploadAntd;
const { TabPane } = Tabs;

let timeout = null;
const _getFileType = (name = '') => {
  let names = name.split('.');
  return names[names.length - 1];
};

const _parsePath = (fileManager, userId, key) => {
  if (key === 'public') {
    return fileManager.currentPath.replace('mlc/public/', '');
  }
  if (key === 'clients') {
    return fileManager.currentPath.replace('mlc/', '');
  }
  return fileManager.currentPath === ''
    ? `${userId}/`
    : fileManager.currentPath.replace(fileManager.parentNode, `${userId}`);
};

const _parseDataObj = (fileManager, key, data) => {
  if (fileManager.currentPath === '') {
    if (key === 'public') {
      return { is_public: false };
    } else {
      return {};
    }
  } else {
    if (key === 'public') {
      return {
        is_public: true,
        custom_path: fileManager.currentPath.replace('mlc/public/', ''),
      };
    } else {
      return data;
    }
  }
};

const _parseRelativePath = (fileManager, values, key) => {
  if (key === 'private') {
    return (
      fileManager.currentPath.replace('mlc/users/', '') + values.name + '/'
    );
  } else if (key === 'public') {
    return (
      fileManager.currentPath.replace('mlc/public/', '') + values.name + '/'
    );
  } else {
    return (
      fileManager.currentPath.replace('mlc/', '') + values.name + '/'
    );
  }
};

const FilesWrapper = ({
  config = {
    selector: {
      body: {},
      handler: () => ({}),
      oneFile: false,
      reminder: false,
    },
    mode: 'full', // Full, lite
  },
}) => {
  const [modal, setModal] = useState(false);
  const [fileId, setFileId] = useState(null);
  const [fileType, setFileType] = useState('');
  const [folderModal, setFolderModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [renameModal, setRenameModal] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [formRef] = Form.useForm();
  const [auth] = useAuth();
  const [folderName, setFolderName] = useState('');
  const [order, setOrder] = useState(1);
  //COntiene el id del usuario para la sección "clients"
  const [externalUserId, setExternalUserId] = useState(undefined);
  //Contiene el nombre del usuario
  const [externalUserName, setExternalUserName] = useState(undefined);
  const [key, setKey] = useState(
    auth.role.rol !== 'client' ? 'public' : 'private'
  );
  const [fileManager, setFileManager] = useState(
    immutable({
      parentNode: key === 'public' ? '/' : `mlc/users/${auth.user._id}`,
      currentPath: '',
      backPath: [],
    })
  );
  const [folderLoading, setFolderLoading] = useState(false);
  const [files, loading, , update, , find] = useFetchUserFilesPath(
    auth.role.rol,
    key === 'clients' ? externalUserId : auth.user._id,
    _parsePath(
      fileManager,
      key === 'clients' ? externalUserId : auth.user._id,
      key
    ),
    key
  );
  const { body, handler, oneFile, reminder } = config.selector;

  const data = {
    custom_path: fileManager.currentPath
      .replace('mlc/users/', 'users/')
      .replace('mlc/public/', ''),
  };
  const props = {
    fileList,
    name: 'files',
    multiple: true,
    action: `${getServer()}/multimedia`,
    data: _parseDataObj(fileManager, key, data),
    accept: process.env.REACT_APP_MUL_ALLOW_EXTENSIONS || 'image/*',
    headers: {
      Authorization: `bearer ${auth.token}`,
    },
    onChange(info) {
      const { status } = info.file;
      setFileList(info.fileList);
      if (status === 'done') {
        message.success(
          `Se ha subido el archivo ${info.file.name}  exitosamente.`
        );
        if (config.mode === 'lite') {
          let newFiles = [];
          //Soporte para multiples archivos (cada fileList tiene su arreglo de response con los datos)
          for (let fileRs of info.fileList) {
            if (Array.isArray(fileRs.response)) {
              for (let res of fileRs.response) {
                newFiles.push(res);
              }
            }
          }
          handler({
            ...body,
            files: [...body.files, ...newFiles],
          });
        }
        if (
          info.fileList.filter(item => item.status === 'done').length ===
          info.fileList.length
        ) {
          setFileList([]);
          update();
        }
        setModal(false);
      } else if (status === 'error') {
        message.error(`Error subiendo el archivo ${info.file.name}.`);
      }
    },
  };

  const _confirmDelete = (file_id, type = 'file', event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    Modal.confirm({
      title:
        '¿Estás seguro de que quieres borrar ' +
        (type === 'file' ? 'este archivo?' : 'está carpeta?'),
      content:
        (type === 'file'
          ? 'Los archivos eliminados'
          : 'Las carpetas eliminadas') + ' no se podrán recuperar',
      okText: 'Si',
      cancelText: 'No',
      onOk: async () => {
        if (type === 'file') {
          await deleteFile(file_id).then(response => {
            if (response.ok) {
              message.success('Archivo eliminado');
              update();
            } else {
              message.error('No se pudo eliminar el archivo');
            }
          });
        } else {
          await deleteFolder(file_id).then(response => {
            if (response.ok) {
              message.success('Carpeta eliminada');
              update();
            } else {
              message.error('No se pudo eliminar la carpeta');
            }
          });
        }
      },
    });
  };

  const _filterPathFiles = () => {
    let { parentNode, currentPath } = fileManager,
      aFiles = [],
      aFolders = [],
      newArray = [];

    for (let path of files.CommonPrefixes) {
      let preKey = path.Prefix.replace(parentNode, '').split('/');
      let fakeKey = path?.FakePath?.replace(parentNode, '').split('/');
      aFolders.push({
        ...path,
        type: 'Folder',
        RealName: fakeKey
          ? fakeKey[fakeKey.length - 2]
          : preKey[preKey.length - 2] || '1',
      });
    }

    for (let path of files.Contents) {
      if (path.RealName) {
        aFiles.push({
          ...path,
          type: 'File',
        });
      }
    }
    // Order with Natural Sort
    aFiles = naturalSort(aFiles, order);
    aFolders = naturalSort(aFolders, order);

    if (currentPath !== '') {
      newArray.push({ type: 'Back' });
    }

    newArray = newArray.concat(aFolders);
    newArray = newArray.concat(aFiles);

    return loading ? [] : newArray;
  };
  const _handleSearchFile = name => {
    clearTimeout(timeout);
    if (name.trim() !== '') {
      timeout = setTimeout(() => {
        find(name, fileManager.parentNode);
      }, 1000);
    } else {
      timeout = setTimeout(() => {
        update();
      }, 1000);
    }
  };

  const _createFolder = async values => {
    let response;
    setFolderLoading(true);
    switch (key) {
      case 'public':
        response = await createFolder(
          _parseRelativePath(fileManager, values, key),
          { is_public: true }
        );
        break;
      case 'private':
        response = await createFolder(
          _parseRelativePath(fileManager, values, key)
        );
        break;
      default:
        response = await createFolder(
          fileManager.currentPath.replace('mlc/', '') + values.name + '/'
        );
    }
    if (response?.ok) {
      update();
      formRef.resetFields();
      setFolderModal(false);
    }
    setFolderLoading(false);
  };
  const _updateFile = async ({ name }) => {
    setModalLoading(true);
    let response = await updateFile(fileId, { name: `${name}.${fileType}` });
    if (response.ok) {
      update();
      formRef.resetFields();
      setRenameModal(false);
      setFileId(null);
      setFileType('');
    }
    setModalLoading(false);
  };
  const _handleClickEditName = item => {
    const core = item.RealName.split('.');
    const type = core[core.length - 1];
    let name = '';
    for (let i = 0; i < core.length - 1; i++) {
      name += core[i];
      if (i < core.length - 2) {
        name += '.';
      }
    }
    setFileId(item._id);
    setRenameModal(true);
    setFileType(type);
    formRef.setFieldsValue({
      name: name,
    });
  };
  useEffect(() => {
    let sCurrent = '';
    if (fileManager) {
      if (fileManager.currentPath) {
        if (fileManager.parentNode) {
          sCurrent = fileManager.currentPath.replace(
            fileManager.parentNode,
            ''
          );
        }
      }
    }
    if (externalUserId) {
      sCurrent = fileManager?.currentPath?.replace(
        `mlc/users/${externalUserId}`,
        externalUserName
      );
    }
    if (key === 'public') {
      sCurrent = fileManager?.currentPath?.replace(`mlc/public/`, '/');
    }
    if (key === 'private') {
      sCurrent = fileManager?.currentPath?.replace(
        `mlc/users/${auth.user._id}`,
        ''
      );
    }
    setBreadcrumb(sCurrent.split('/'));
  }, [fileManager, externalUserId, externalUserName, key, auth.user._id]);
  const _removeFileInList = id => {
    handler({
      ...body,
      files: body.files.filter(e => e._id !== id),
    });
  };
  const _setFileInList = file => {
    handler({
      ...body,
      files: [...body.files, file],
    });
  };
  const _setReminder = values => {
    for (let index of Object.keys(values)) {
      if (index === 'period_time') {
        values[index] = values[index].format('hh:mm a');
      }
    }
    handler({ ...body, reminders: { ...body.reminders, ...values } });
  };
  const _verifyReminder = () =>
    (auth.role.rol === 'employee' || auth.role.rol === 'leader') && reminder;

  const _renderFiles = () => {
    if (loading) {
      return <Skeleton active />;
    }
    return (
      <div className="file" style={{ height: 250, overflow: 'auto' }}>
        <List
          itemLayout="horizontal"
          dataSource={_filterPathFiles()}
          locale={{ emptyText: 'Sin Archivos' }}
          loading={loading}
          pagination={{ defaultPageSize: 100 }}
          renderItem={item => {
            if (item.type === 'Folder') {
              return (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  className="folder-element"
                  onClick={() =>
                    setFileManager(
                      fileManager.merge({
                        currentPath: item.Prefix,
                        backPath: [
                          ...fileManager.backPath,
                          fileManager.currentPath,
                        ],
                      })
                    )
                  }
                >
                  <List.Item.Meta
                    avatar={
                      <FileIcon
                        fileType="folder"
                        style={{ fontSize: 30 }}
                        twoToneColor="#60A50D"
                      />
                    }
                    title={item.RealName}
                  />
                </List.Item>
              );
            }
            if (item.type === 'Back') {
              return (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  className="back-element"
                  onClick={() =>
                    setFileManager(
                      fileManager.merge({
                        currentPath:
                          fileManager.backPath[fileManager.backPath.length - 1],
                        backPath: fileManager.backPath.slice(
                          0,
                          fileManager.backPath.length - 1
                        ),
                      })
                    )
                  }
                >
                  <List.Item.Meta
                    avatar={
                      <FileIcon
                        fileType="arrow-l"
                        style={{ color: '#7AB32B' }}
                        size={22}
                        strokeWidth={2}
                      />
                    }
                    title="Regresar"
                  />
                </List.Item>
              );
            }
            return (
              <List.Item
                actions={[
                  <Checkbox
                    key="select"
                    disabled={
                      oneFile &&
                      body.files.length === 1 &&
                      body.files[0]._id !== item._id
                    }
                    checked={body.files.some(e => e._id === item._id)}
                    onChange={e => {
                      if (e.target.checked) {
                        _setFileInList({ name: item.RealName, _id: item._id });
                      } else {
                        _removeFileInList(item._id);
                      }
                    }}
                  />,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <FileIcon
                      fileType={getFileType(item.RealName)}
                      style={{ fontSize: 30 }}
                      twoToneColor="#60A50D"
                    />
                  }
                  title={item.RealName}
                />
              </List.Item>
            );
          }}
        />
      </div>
    );
  };
  const _renderBody = () => {
    if (config.mode === 'lite') {
      return (
        <div>
          <Tabs defaultActiveKey="1">
            <TabPane tab="Archivos" key="1">
              <div>
                <span
                  style={{
                    display: 'inline-block',
                    marginRight: 10,
                    marginBottom: 20,
                  }}
                >
                  Directorio:
                </span>
                {breadcrumb.length && (
                  <Breadcrumb
                    style={{
                      display: 'inline-block',
                    }}
                  >
                    <Breadcrumb.Item>
                      <HomeOutlined />
                    </Breadcrumb.Item>
                    {breadcrumb.map(item => (
                      <Breadcrumb.Item key={item}>{item}</Breadcrumb.Item>
                    ))}
                  </Breadcrumb>
                )}
                <Row justify="space-between" gutter={[10, 10]}>
                  <Col span={12}>
                    <Input
                      placeholder="Nombre de la carpeta"
                      value={folderName}
                      onChange={e => setFolderName(e.target.value)}
                    />
                  </Col>
                  <Col span={12} style={{ textAlign: 'right' }}>
                    <Button
                      icon={
                        <FolderPlus
                          size={14}
                          strokeWidth={2}
                          className="anticon"
                        />
                      }
                      disabled={folderName === ''}
                      onClick={_createFolder}
                    >
                      Crear carpeta
                    </Button>
                  </Col>
                </Row>
              </div>
              <div>{_renderFiles()}</div>
            </TabPane>
            <TabPane tab="Subir Archivo" key="2">
              <span
                style={{
                  display: 'inline-block',
                  marginRight: 10,
                  marginBottom: 20,
                }}
              >
                Directorio:
              </span>
              {breadcrumb.length && (
                <Breadcrumb
                  style={{
                    display: 'inline-block',
                  }}
                >
                  <Breadcrumb.Item>
                    <HomeOutlined />
                  </Breadcrumb.Item>
                  {breadcrumb.map(item => (
                    <Breadcrumb.Item key={item}>{item}</Breadcrumb.Item>
                  ))}
                </Breadcrumb>
              )}
              <div style={{ minHeight: 250 }}>
                <Dragger {...props}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click o arrastra el archivo en esta área para subir
                  </p>
                  <p className="ant-upload-hint">
                    Los archivos se subirán automáticamente
                  </p>
                </Dragger>
              </div>
            </TabPane>
            {_verifyReminder() && (
              <TabPane tab={`Recordatorio`} key="3">
                <div style={{ minHeight: 250 }}>
                  <ReminderForm onValuesChange={_setReminder} />
                </div>
              </TabPane>
            )}
          </Tabs>
          <Input
            placeholder={oneFile ? 'Nombre' : 'Descripción'}
            style={{ marginTop: 20 }}
            value={body.description}
            onChange={e => handler({ ...body, description: e.target.value })}
          />
          <List
            style={{ marginTop: 20 }}
            bordered
            pagination={{ defaultPageSize: 100 }}
            dataSource={body.files}
            locale={{ emptyText: 'Sin archivos seleccionados' }}
            renderItem={item => (
              <List.Item key={item._id}>{item.name}</List.Item>
            )}
          />
        </div>
      );
    }
    return (
      <div className="content">
        <Row justify="space-between">
          <Col>
            {(fileManager.backPath.length > 0 || key !== 'clients') && (
              <>
                <Button
                  type="primary"
                  icon={
                    <Upload size={14} strokeWidth={2} className="anticon" />
                  }
                  onClick={() => setModal(true)}
                  style={{ marginRight: 8 }}
                >
                  Subir archivo
                </Button>
                <Button
                  onClick={() => setFolderModal(true)}
                  icon={
                    <FolderPlus size={14} strokeWidth={2} className="anticon" />
                  }
                >
                  Crear carpeta
                </Button>
              </>
            )}
          </Col>
          <Col>
            <Row gutter={[10, 0]}>
              <Col>
                <Radio.Group
                  options={[
                    {
                      label: <SortAscendingOutlined />,
                      value: 1,
                    },
                    {
                      label: <SortDescendingOutlined />,
                      value: -1,
                    },
                  ]}
                  onChange={e => setOrder(e.target.value)}
                  value={order}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Col>
              <Col>
                <Input
                  placeholder="Buscar archivo"
                  onChange={e => _handleSearchFile(e.target.value)}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row
          style={{
            marginTop: 10,
            marginBottom: 10,
          }}
        >
          <Col>
            <span
              style={{
                display: 'inline-block',
                marginRight: 10,
              }}
            >
              Directorio:
            </span>
            {breadcrumb.length && (
              <Breadcrumb
                style={{
                  display: 'inline-block',
                }}
              >
                <Breadcrumb.Item>
                  <HomeOutlined />
                </Breadcrumb.Item>
                {breadcrumb.map(item => (
                  <Breadcrumb.Item key={item}>{item}</Breadcrumb.Item>
                ))}
              </Breadcrumb>
            )}
          </Col>
        </Row>
        <div className="file">
          <List
            itemLayout="horizontal"
            dataSource={_filterPathFiles()}
            locale={{ emptyText: 'Sin Archivos' }}
            loading={loading}
            pagination={{ defaultPageSize: 100 }}
            renderItem={item => {
              if (item.type === 'Folder') {
                return (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    className="folder-element"
                    actions={[
                      <Button
                        danger
                        type="primary"
                        onClick={event =>
                          _confirmDelete(item.Prefix, 'folder', event)
                        }
                        icon={
                          <Trash
                            size={14}
                            strokeWidth={2}
                            className="anticon"
                          />
                        }
                      />,
                    ]}
                    onClick={() => {
                      setFileManager(
                        fileManager.merge({
                          currentPath: item.Prefix,
                          backPath: [
                            ...fileManager.backPath,
                            fileManager.currentPath,
                          ],
                        })
                      );
                      if (fileManager.currentPath.length === 0) {
                        setExternalUserId(
                          item?.Prefix?.replace('mlc/users/', '')
                        );
                        setExternalUserName(
                          item?.FakePath?.replace('mlc/users/', '')
                        );
                      }
                    }}
                  >
                    <List.Item.Meta
                      avatar={
                        <FileIcon
                          fileType="folder"
                          style={{ fontSize: 30 }}
                          twoToneColor="#60A50D"
                        />
                      }
                      title={item.RealName}
                    />
                  </List.Item>
                );
              }
              if (item.type === 'Back') {
                return (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    className="back-element"
                    onClick={() => {
                      if (key === 'clients') {
                        if (fileManager.backPath.length === 1) {
                          setExternalUserId(undefined);
                          setExternalUserName(
                            item?.FakePath?.replace('mlc/users/', '')
                          );
                        }
                      }
                      setFileManager(
                        fileManager.merge({
                          currentPath:
                            fileManager.backPath[
                              fileManager.backPath.length - 1
                            ],
                          backPath: fileManager.backPath.slice(
                            0,
                            fileManager.backPath.length - 1
                          ),
                        })
                      );
                    }}
                  >
                    <List.Item.Meta
                      avatar={
                        <FileIcon
                          fileType="arrow-l"
                          style={{ color: '#7AB32B' }}
                          size={22}
                          strokeWidth={2}
                        />
                      }
                      title="Regresar"
                    />
                  </List.Item>
                );
              }

              return (
                <List.Item
                  actions={[
                    <PreviewButton
                      path={item.Key.replace('mlc/', '')}
                      fileName={item.RealName}
                      fileType={item.FileType}
                    />,
                    <LoadingButton
                      path={item.Key.replace('mlc/', '')}
                      fileName={item.RealName}
                    />,
                    <Button
                      type="dashed"
                      onClick={() => {
                        _handleClickEditName(item);
                      }}
                      icon={
                        <Edit size={14} strokeWidth={2} className="anticon" />
                      }
                    />,
                    <Button
                      danger
                      type="primary"
                      onClick={() => _confirmDelete(item._id)}
                      icon={
                        <Trash size={14} strokeWidth={2} className="anticon" />
                      }
                    />,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <FileIcon
                        fileType={_getFileType(item.RealName)}
                        style={{ fontSize: 30 }}
                        twoToneColor="#60A50D"
                      />
                    }
                    title={item.RealName}
                    description={
                      <>
                        <div>{`Fecha de modificación: ${dayjs(files.LastModified).format('DD/MM/YYYY hh:ss a')}`}</div>
                        <div>{`${item.Size === -1 ? 'Ruta: ' : 'Tamaño: '} ${item.Size === -1 ? item?.FakePath || '/': fileSize(item.Size)}`}</div>
                      </>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </div>
      </div>
    );
  };
  return (
    <div className="slideInLeft">
      <Modal
        title="Subir nuevo archivo"
        visible={modal}
        onCancel={() => setModal(false)}
        footer={[]}
      >
        <Dragger {...props}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click o arrastra el archivo en esta área para subir
          </p>
          <p className="ant-upload-hint">
            Los archivos se subir&aacute;n automaticamente
          </p>
        </Dragger>
      </Modal>
      <Modal
        title="Nueva carpeta"
        visible={folderModal}
        onOk={() => formRef.submit()}
        onCancel={() => {
          setFolderModal(false);
          formRef.resetFields();
        }}
        okText="Crear"
        cancelText="Cancelar"
        okButtonProps={{ loading: folderLoading }}
      >
        <FolderForm formRef={formRef} onSubmit={_createFolder} />
      </Modal>
      <Modal
        title="Renombrar archivo"
        visible={renameModal}
        onOk={() => {
          setFolderModal(false);
          formRef.submit();
        }}
        onCancel={() => {
          setRenameModal(false);
          setFileId(null);
          formRef.resetFields();
        }}
        okText="Renombrar"
        okButtonProps={{ loading: modalLoading }}
        cancelText="Cancelar"
      >
        <FolderForm
          formRef={formRef}
          onSubmit={_updateFile}
          label="Nombre del archivo"
        />
      </Modal>
      <Tabs
        defaultActiveKey="public"
        activeKey={key}
        onChange={e => {
          setKey(e);
          setExternalUserId(undefined);
          setExternalUserName(undefined);
          setFileManager(
            immutable({
              parentNode: `mlc/users/${auth.user._id}`,
              currentPath: '',
              backPath: [],
            })
          );
        }}
      >
        {auth.role.rol !== 'client' && (
          <>
            <TabPane tab="Públicos" key="public"></TabPane>
            <TabPane tab="Clientes" key="clients"></TabPane>
          </>
        )}
        {auth.role.rol === 'client' && (
          <TabPane tab="Personales" key="private"></TabPane>
        )}
      </Tabs>
      {_renderBody()}
    </div>
  );
};

export default FilesWrapper;
